import { getAgentUrl } from './agent-url'
import { authHeaders } from './auth-headers'

async function adminFetch(path: string, opts?: RequestInit): Promise<Response> {
  const baseUrl = await getAgentUrl()
  const headers = { ...await authHeaders(), ...opts?.headers }
  return fetch(`${baseUrl}${path}`, { ...opts, headers })
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string
  email: string
  name: string | null
  plan: string
  jobs_per_month: number
  job_count: number
  created_at: string
}

export interface AdminUserDetail {
  user: { id: string; email: string; name: string | null; created_at: string }
  quota: { plan: string; jobs_per_month: number } | null
  jobs: Array<{ id: string; label: string; material: string; created_at: string; closed_at: string | null }>
  recentMessages: Array<{ id: string; job_id: string; role: string; content: string; created_at: string }>
  recentUsage: Array<{ model: string; input_tokens: number; output_tokens: number; total_tokens: number; cost_usd: string; created_at: string }>
}

export interface AdminInvite {
  id: string
  token: string
  email: string | null
  max_uses: number
  uses_count: number
  expires_at: string | null
  created_at: string
  redemption_count: number
}

export interface AdminUsageSummary {
  totals: {
    total_calls: number
    input_tokens: number
    output_tokens: number
    total_tokens: number
    total_cost: number
  }
  byModel: Array<{ model: string; calls: number; tokens: number; cost: number }>
  byDay: Array<{ day: string; calls: number; tokens: number; cost: number }>
}

export interface AdminUsageRow {
  id: string
  user_id: string
  email: string | null
  model: string
  call_site: string
  input_tokens: number
  output_tokens: number
  total_tokens: number
  cost_usd: string
  created_at: string
}

export interface EmulatedUser {
  user: { id: string; email: string; name: string | null; created_at: string }
  quota: { plan: string; jobs_per_month: number } | null
  jobs: Array<Record<string, unknown> & { messages: Array<Record<string, unknown>> }>
  machineProfiles: Array<Record<string, unknown>>
  toolLibraries: Array<Record<string, unknown>>
}

// ─── API functions ───────────────────────────────────────────────────────────

export async function fetchUsers(): Promise<AdminUser[]> {
  const res = await adminFetch('/api/admin/users')
  if (res.status === 403) throw new Error('forbidden')
  if (!res.ok) throw new Error('Failed to fetch users')
  return res.json()
}

export async function fetchUserDetail(id: string): Promise<AdminUserDetail> {
  const res = await adminFetch(`/api/admin/users/${encodeURIComponent(id)}`)
  if (!res.ok) throw new Error('Failed to fetch user detail')
  return res.json()
}

export async function updateUserQuota(id: string, data: { plan?: string; jobs_per_month?: number }): Promise<void> {
  const res = await adminFetch(`/api/admin/users/${encodeURIComponent(id)}/quota`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error('Failed to update quota')
}

export async function fetchInvites(): Promise<AdminInvite[]> {
  const res = await adminFetch('/api/admin/invites')
  if (!res.ok) throw new Error('Failed to fetch invites')
  return res.json()
}

export async function createInvite(data: { email?: string; maxUses?: number; expiresInDays?: number }): Promise<AdminInvite> {
  const res = await adminFetch('/api/admin/invites', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error('Failed to create invite')
  return res.json()
}

export async function deleteInvite(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/invites/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  if (!res.ok) throw new Error('Failed to delete invite')
}

export async function fetchUsageSummary(): Promise<AdminUsageSummary> {
  const res = await adminFetch('/api/admin/usage/summary')
  if (!res.ok) throw new Error('Failed to fetch usage summary')
  return res.json()
}

export async function fetchUsageRecent(limit = 50, offset = 0): Promise<AdminUsageRow[]> {
  const res = await adminFetch(`/api/admin/usage/recent?limit=${limit}&offset=${offset}`)
  if (!res.ok) throw new Error('Failed to fetch recent usage')
  return res.json()
}

export async function fetchEmulatedUser(userId: string): Promise<EmulatedUser> {
  const res = await adminFetch(`/api/admin/emulate/${encodeURIComponent(userId)}`)
  if (!res.ok) throw new Error('Failed to fetch emulated user')
  return res.json()
}

// ─── Docs ───────────────────────────────────────────────────────────────────

export interface DocArticle {
  id: string
  slug: string
  title: string
  content: string
  sort_order: number
  published: boolean
  created_at: string
  updated_at: string
}

export async function fetchAdminDocs(): Promise<DocArticle[]> {
  const res = await adminFetch('/api/admin/docs')
  if (!res.ok) throw new Error('Failed to fetch docs')
  return res.json()
}

export async function createDoc(data: { title: string; slug: string; content?: string; published?: boolean }): Promise<DocArticle> {
  const res = await adminFetch('/api/admin/docs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error('Failed to create doc')
  return res.json()
}

export async function updateDoc(id: string, data: { title?: string; slug?: string; content?: string; published?: boolean; sort_order?: number }): Promise<DocArticle> {
  const res = await adminFetch(`/api/admin/docs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error('Failed to update doc')
  return res.json()
}

export async function deleteDoc(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/docs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  if (!res.ok) throw new Error('Failed to delete doc')
}
